import { billAmountContent } from '@/content/billAmountContent';
import { BillAmountProvider, FieldName } from '@/state/state.types';
import { formatCurrency, parseCurrency } from '@/utils/formatCurrency';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BillAmountRow,
  MAX_ROWS,
  createEmptyRow,
  isProviderDuplicate,
} from '@/utils/billAmountForm.utils';
import {
  buildFormData,
  validateAllRows,
  validateRowField,
} from '@/utils/billAmountForm.validation';
import { useFields } from './useFields';

export type { BillAmountRow } from '@/utils/billAmountForm.utils';
export { mapDropdownToProvider } from '@/utils/billAmountForm.utils';

export const useBillAmountForm = () => {
  const [rows, setRows] = useState<BillAmountRow[]>([createEmptyRow()]);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const fields = useFields();

  useEffect(() => {
    const raw = fields?.[FieldName.bill_amount];
    if (typeof raw !== 'string' || !raw) return;
    try {
      const entries = JSON.parse(raw) as { amt: number; provider: BillAmountProvider }[];
      if (!Array.isArray(entries) || entries.length === 0) return;
      setRows(
        entries.map((entry) => ({
          key: nanoid(),
          amountDisplay: entry.amt.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
          amount: entry.amt,
          provider: entry.provider,
          isCustomProvider: !entry.provider.id,
          providerSearchTerm: entry.provider.name,
          errors: {},
        })),
      );
    } catch {
      // ignore parse errors, keep default empty row
    }
  }, []);

  const updateRow = useCallback((key: string, updater: (row: BillAmountRow) => BillAmountRow) => {
    setRows((prev) => prev.map((row) => (row.key === key ? updater(row) : row)));
  }, []);

  const addRow = useCallback(() => {
    setRows((prev) => {
      if (MAX_ROWS !== -1 && prev.length >= MAX_ROWS) return prev;
      return [...prev, createEmptyRow()];
    });
  }, []);

  const removeRow = useCallback((key: string) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((row) => row.key !== key)));
  }, []);

  const updateAmount = useCallback(
    (key: string, rawInput: string) => {
      const display = formatCurrency(rawInput);
      const amount = parseCurrency(rawInput);
      updateRow(key, (row) => ({
        ...row,
        amountDisplay: display,
        amount,
        errors: { ...row.errors, amount: undefined },
      }));
    },
    [updateRow],
  );

  const setProviderWithDuplicateCheck = useCallback(
    (key: string, provider: BillAmountProvider, isCustom: boolean) => {
      if (isProviderDuplicate(rowsRef.current, key, provider)) {
        updateRow(key, (row) => ({
          ...row,
          provider: null,
          isCustomProvider: false,
          providerSearchTerm: '',
          errors: { ...row.errors, provider: billAmountContent.errors.providerDuplicate },
        }));
        return;
      }
      updateRow(key, (row) => ({
        ...row,
        provider,
        isCustomProvider: isCustom,
        providerSearchTerm: provider.name,
        errors: { ...row.errors, provider: undefined },
      }));
    },
    [updateRow],
  );

  const updateProvider = useCallback(
    (key: string, provider: BillAmountProvider) =>
      setProviderWithDuplicateCheck(key, provider, false),
    [setProviderWithDuplicateCheck],
  );

  const updateCustomProvider = useCallback(
    (key: string, provider: BillAmountProvider) =>
      setProviderWithDuplicateCheck(key, provider, true),
    [setProviderWithDuplicateCheck],
  );

  const clearProvider = useCallback(
    (key: string) => {
      updateRow(key, (row) => ({
        ...row,
        provider: null,
        isCustomProvider: false,
        providerSearchTerm: '',
      }));
    },
    [updateRow],
  );

  const validateRow = useCallback(
    (key: string, field: 'amount' | 'provider') => {
      updateRow(key, (row) => validateRowField(row, field));
    },
    [updateRow],
  );

  const validate = useCallback((): boolean => {
    const result = validateAllRows(rowsRef.current);
    setRows(result.rows);
    return result.isValid;
  }, []);

  const formTransformer = useCallback(
    (stepId: string) => () => buildFormData(stepId, rowsRef.current),
    [],
  );

  const rowHasData = useCallback((row: BillAmountRow): boolean => {
    return row.amount > 0 || row.provider !== null;
  }, []);

  return {
    rows,
    addRow,
    removeRow,
    updateAmount,
    updateProvider,
    updateCustomProvider,
    clearProvider,
    validateRow,
    validate,
    formTransformer,
    rowHasData,
  };
};
